import React, { useContext, useEffect, useState } from 'react';
import Layout from '../elements/Layout';
import { fetchAdminAnalytics } from '../utils/api/api';
import { AuthContext } from '../utils/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { canAccess } from '../utils/canAccess';

// Icons
import { IoAddOutline } from 'react-icons/io5';
import {
  FaRegClock,
  FaChartLine,
  FaMoneyBillWave,
  FaUsers,
  FaRegCalendarAlt,
  FaRegBell,
  FaEllipsisH,
  FaArrowUp,
  FaArrowDown,
  FaTasks,
  FaHistory,
  FaRegFileAlt,
  FaClipboardList,
  FaExternalLinkAlt,
  FaPlus
} from 'react-icons/fa';
import { MdAttachMoney, MdMoneyOff, MdTrendingUp, MdTrendingDown, MdPeople, MdDashboard } from 'react-icons/md';

// Import components (these would be our redesigned versions of the components)
import DashStats from '../components/DashStats';
import SalesRevenue from '../components/SalesRevenue';
import CustomerEngagementBarGraph from '../components/dashboard/CustomerEngagementBarGraph';
import ProgressTrackerTable from '../components/dashboard/ProgressTrackerTable';
import InvoiceTable from '../components/dashboard/InvoiceTable';

const Dashboard = () => {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const getAnalytics = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminAnalytics();
      setAnalytics(data.statistics);
    } catch (err) {
      console.log("An error occurred fetching analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAnalytics();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    }
  };

  const statCardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
    hover: {
      y: -5,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 15
      }
    }
  };

  const formatCurrency = (amount) => {
    return Number(amount).toLocaleString("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Determine if values are increasing or decreasing (for demo purposes)
  const isIncreasing = (value) => {
    return value > 0;
  };

  // For skeleton loading
  const Skeleton = ({ className }) => (
    <motion.div
      initial={{ opacity: 0.5 }}
      animate={{ opacity: [0.5, 0.8, 0.5] }}
      transition={{ duration: 1.5, repeat: Infinity }}
      className={`bg-gray-200 rounded-lg ${className}`}
    />
  );

  return (
    <Layout text="Dashboard">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="space-y-6"
      >
        {/* Dashboard Header */}
        <motion.div
          variants={itemVariants}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm p-6 flex flex-col md:flex-row justify-between items-center gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-white text-lg font-semibold">
              {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
            </div>
            <div>
              <motion.h1
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl font-bold text-gray-800"
              >
                Hello, {user?.first_name}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-gray-500 text-sm"
              >
                Welcome back to your dashboard
              </motion.p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="px-4 py-2 bg-gray-50 rounded-xl text-gray-500 text-sm hidden md:block">
              {moment(currentTime).format('dddd, MMMM D, YYYY')}
            </div>

            {canAccess(user, 'create_project') && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard/projects/new')}
                className="flex items-center gap-2 px-5 py-2.5 bg-secondary text-black rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
              >
                <FaPlus className="text-black" />
                <span className="font-medium">New Project</span>
              </motion.button>
            )}
          </div>
        </motion.div>

        {canAccess(user, 'view_dashboard_analytics') ? (
          <>
            {/* Main Stats */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <motion.div
                whileHover="hover"
                variants={statCardVariants}
                className="relative overflow-hidden rounded-2xl shadow-sm"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary/80 z-0"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 z-0"></div>
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full -ml-20 -mb-20 z-0"></div>
                <div className="relative z-10 p-6 text-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                          <MdAttachMoney size={24} />
                        </div>
                        <h3 className="text-lg font-medium">§</h3>
                      </div>
                      <div className="flex items-end gap-2">
                        <p className="text-3xl font-bold">
                          {loading ? (
                            <Skeleton className="w-36 h-9" />
                          ) : (
                            formatCurrency(analytics?.total_sales || 0)
                          )}
                        </p>
                        <div className="text-sm pb-1 flex items-center">
                          <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isIncreasing(10) ? 'bg-green-500/20 text-green-100' : 'bg-red-500/20 text-red-100'}`}>
                            {isIncreasing(10) ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                            {isIncreasing(10) ? '+12%' : '-8%'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:block">
                      <FaChartLine size={50} className="text-white/20" />
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-white/70">
                    Total sales value across all properties and projects
                  </p>
                </div>
              </motion.div>

              <motion.div
                whileHover="hover"
                variants={statCardVariants}
                className="relative overflow-hidden rounded-2xl shadow-sm"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-secondary to-secondary/80 z-0"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 z-0"></div>
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full -ml-20 -mb-20 z-0"></div>
                <div className="relative z-10 p-6 text-primary">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-white/30 backdrop-blur-sm rounded-xl">
                          <FaMoneyBillWave size={24} className="text-primary" />
                        </div>
                        <h3 className="text-lg font-medium">Total Income</h3>
                      </div>
                      <div className="flex items-end gap-2">
                        <p className="text-3xl font-bold">
                          {loading ? (
                            <Skeleton className="w-36 h-9" />
                          ) : (
                            formatCurrency(analytics?.total_income || 0)
                          )}
                        </p>
                        <div className="text-sm pb-1 flex items-center">
                          <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isIncreasing(15) ? 'bg-green-500/20 text-green-800' : 'bg-red-500/20 text-red-800'}`}>
                            {isIncreasing(15) ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                            {isIncreasing(15) ? '+15%' : '-5%'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:block">
                      <MdTrendingUp size={50} className="text-primary/20" />
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-primary/70">
                    Total income received from sales and other revenue streams
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Additional Stats */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6"
            >
              <motion.div
                whileHover="hover"
                variants={statCardVariants}
                className="bg-white/80 shadow-sm backdrop-blur-sm p-5 rounded-2xl"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-2 bg-blue-500/10 text-blue-500 rounded-xl">
                        <MdPeople size={20} />
                      </div>
                      <p className="text-sm font-medium text-gray-600">Customers</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">
                      {loading ? (
                        <Skeleton className="w-20 h-8" />
                      ) : (
                        analytics?.total_customers || 198
                      )}
                    </p>
                  </div>
                  <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isIncreasing(8) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {isIncreasing(8) ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                    {isIncreasing(8) ? '+8%' : '-3%'}
                  </span>
                </div>
                <div className="mt-4 w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '78%' }}
                    transition={{ delay: 0.5, duration: 1 }}
                    className="h-full bg-blue-500"
                  />
                </div>
              </motion.div>

              <motion.div
                whileHover="hover"
                variants={statCardVariants}
                className="bg-white/80 shadow-sm backdrop-blur-sm p-5 rounded-2xl"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-2 bg-green-500/10 text-green-500 rounded-xl">
                        <FaRegFileAlt size={20} />
                      </div>
                      <p className="text-sm font-medium text-gray-600">Contracts</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">
                      {loading ? (
                        <Skeleton className="w-20 h-8" />
                      ) : (
                          analytics?.active_contracts || 0
                      )}
                    </p>
                  </div>
                  <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isIncreasing(12) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {isIncreasing(12) ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                    {isIncreasing(12) ? '+12%' : '-4%'}
                  </span>
                </div>
                <div className="mt-4 w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '65%' }}
                    transition={{ delay: 0.5, duration: 1 }}
                    className="h-full bg-green-500"
                  />
                </div>
              </motion.div>

              <motion.div
                whileHover="hover"
                variants={statCardVariants}
                className="bg-white/80 shadow-sm backdrop-blur-sm p-5 rounded-2xl"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-2 bg-purple-500/10 text-purple-500 rounded-xl">
                        <FaRegBell size={20} />
                      </div>
                      <p className="text-sm font-medium text-gray-600">Leads</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">
                      {loading ? (
                        <Skeleton className="w-20 h-8" />
                      ) : (
                        Number(analytics?.active_leads)?.toLocaleString('KES') || 0
                      )}
                    </p>
                  </div>
                  <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isIncreasing(5) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {isIncreasing(5) ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                    {isIncreasing(5) ? '+5%' : '-2%'}
                  </span>
                </div>
                <div className="mt-4 w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '45%' }}
                    transition={{ delay: 0.5, duration: 1 }}
                    className="h-full bg-purple-500"
                  />
                </div>
              </motion.div>

              <motion.div
                whileHover="hover"
                variants={statCardVariants}
                className="bg-white/80 shadow-sm backdrop-blur-sm p-5 rounded-2xl"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-2 bg-amber-500/10 text-amber-500 rounded-xl">
                        <MdDashboard size={20} />
                      </div>
                      <p className="text-sm font-medium text-gray-600">Projects</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">
                      {loading ? (
                        <Skeleton className="w-20 h-8" />
                      ) : (
                        analytics?.projects || 0
                      )}
                    </p>
                  </div>
                  <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isIncreasing(3) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {isIncreasing(3) ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                    {isIncreasing(3) ? '+3%' : '-1%'}
                  </span>
                </div>
                <div className="mt-4 w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '85%' }}
                    transition={{ delay: 0.5, duration: 1 }}
                    className="h-full bg-amber-500"
                  />
                </div>
              </motion.div>
            </motion.div>

            {/* Original DashStats component - we'd replace this with our updated component */}
            {/* <DashStats analytics={analytics} /> */}

            {/* Charts and Tasks Section */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {/* Customer Engagement Chart */}
              <motion.div
                variants={itemVariants}
                className="col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
              >
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-800">Customer Engagement</h2>
                  <p className="text-sm text-gray-500">Monthly customer interaction statistics</p>
                </div>
                <div className="p-4">
                  {loading ? (
                    <div className="h-64 flex items-center justify-center">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full"
                      />
                    </div>
                  ) : (
                    <CustomerEngagementBarGraph customer_engagements={analytics?.customer_engagements} />
                  )}
                </div>
              </motion.div>

              {/* Recent Tasks */}
              <motion.div
                variants={itemVariants}
                className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <FaTasks className="text-primary" />
                    <h2 className="text-lg font-semibold text-gray-800">Recent Tasks</h2>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-1 text-secondary text-sm font-medium"
                  >
                    <FaPlus size={12} />
                    Add Task
                  </motion.button>
                </div>
                <div className="p-4">
                  {loading ? (
                    <div className="space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map((_, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 * index }}
                          className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 border border-gray-100"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`rounded-lg h-10 w-10 flex items-center justify-center ${index % 2 === 0 ? 'bg-red-100 text-red-500' : 'bg-amber-100 text-amber-500'
                              }`}>
                              <FaRegClock />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-700">Call new lead</p>
                              <p className="text-xs text-gray-500">Due in 3 hours</p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${index % 2 === 0 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
                            }`}>
                            {index % 2 === 0 ? 'Urgent' : 'High'}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>

            {/* Sales Analytics and Recent Activities */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {/* Sales Analytics */}
              <motion.div
                variants={itemVariants}
                className="col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
              >
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-800">Sales Analytics</h2>
                  <p className="text-sm text-gray-500">Revenue trends over time</p>
                </div>
                <div className="p-4">
                  {loading ? (
                    <div className="h-64 flex items-center justify-center">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full"
                      />
                    </div>
                  ) : (
                    <SalesRevenue salesAnalytics={analytics?.sales_analytics} text="" />
                  )}
                </div>
              </motion.div>

              {/* Recent Activities */}
              <motion.div
                variants={itemVariants}
                className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <FaHistory className="text-primary" />
                    <h2 className="text-lg font-semibold text-gray-800">Recent Activities</h2>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/dashboard/audit-trail')}
                    className="flex items-center gap-1 text-secondary text-sm font-medium"
                  >
                    View All
                    <FaExternalLinkAlt size={12} />
                  </motion.button>
                </div>
                <div className="overflow-hidden">
                  {loading ? (
                    <div className="p-4 space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full" />
                      ))}
                    </div>
                  ) : (
                    <div className="max-h-[340px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {analytics?.recent_activities?.slice(0, 5)?.map((activity, index) => (
                            <motion.tr
                              key={activity.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.05 * index }}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-6 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="text-sm font-medium text-gray-900">
                                    {activity?.user?.first_name} {activity?.user?.last_name}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{activity?.action}</div>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                {moment(activity.createdAt).format('MMM DD, HH:mm')}
                              </td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>

            {/* Upcoming Invoices */}
            <motion.div
              variants={itemVariants}
              className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800">Upcoming Invoices</h2>
                <p className="text-sm text-gray-500">Invoices due in the next 30 days</p>
              </div>
              <div className="p-4">
                {loading ? (
                  <div className="h-64 flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full"
                    />
                  </div>
                ) : (
                  <InvoiceTable invoices={analytics?.upcoming_invoices || []} />
                )}
              </div>
            </motion.div>

            {/* Project Progress */}
            <motion.div
              variants={itemVariants}
              className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800">Project Progress</h2>
                <p className="text-sm text-gray-500">Current status of ongoing projects</p>
              </div>
              <div className="p-4">
                {loading ? (
                  <div className="h-64 flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full"
                    />
                  </div>
                ) : (
                  <ProgressTrackerTable projects_progress={analytics.projects_progress || []} />
                )}
              </div>
            </motion.div>
          </>
        ) : (
          <motion.div
            variants={itemVariants}
            className="flex flex-col items-center justify-center py-20 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm"
          >
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <MdDashboard size={40} className="text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Analytics Access Required</h2>
            <p className="text-gray-500 text-center max-w-md">
              You don't have permission to view dashboard analytics. Please contact your administrator for access.
            </p>
          </motion.div>
        )}
      </motion.div>
    </Layout>
  );
};

export default Dashboard;